import { useEffect, useMemo, useRef, useState } from "react";
import { Camera, Upload, X } from "lucide-react";
import { requestPlayerPhoto, errorMessage } from "@/lib/api";
import { invalidateScope } from "@/lib/queryClient";
import type { Player, PlayerPhotoRequest } from "@/lib/types";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { useToast } from "@/components/ui/toast";
import { PlayerAvatar } from "@/features/player-photos/PlayerAvatar";

export type LinkedPlayersStripProps = {
  players: Player[];
  photoRequests?: PlayerPhotoRequest[];
};

function playerLabel(p: Player): string {
  return p.jersey_number
    ? `#${p.jersey_number} ${p.display_name}`
    : p.display_name;
}

export function LinkedPlayersStrip({
  players,
  photoRequests = [],
}: LinkedPlayersStripProps) {
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [activePlayer, setActivePlayer] = useState<Player | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const latestRequestByPlayer = useMemo(() => {
    const map = new Map<string, PlayerPhotoRequest>();
    for (const request of photoRequests) {
      if (!map.has(request.player_id)) map.set(request.player_id, request);
    }
    return map;
  }, [photoRequests]);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  if (players.length === 0) {
    return (
      <p
        className="text-ink-muted text-sm"
        data-testid="feedback-linked-empty"
      >
        No roster player is linked to your account yet. Ask a coach to
        link you.
      </p>
    );
  }

  const reset = () => {
    setActivePlayer(null);
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const submit = async () => {
    if (!activePlayer || !file) return;
    setSubmitting(true);
    try {
      await requestPlayerPhoto(activePlayer.id, file);
      toast.success("Photo request sent");
      await invalidateScope();
      reset();
    } catch (err) {
      toast.error("Couldn't send photo request", {
        description: errorMessage(err),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="feedback-linked-strip">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
          Linked players
        </span>
        {players.map((p) => {
          const latest = latestRequestByPlayer.get(p.id);
          const pending = latest?.status === "pending";
          return (
            <span
              key={p.id}
              data-feedback-linked-player={p.id}
              className="inline-flex items-center gap-2 rounded border border-rule bg-surface px-2 py-1 text-xs text-ink"
            >
              <PlayerAvatar player={p} size="sm" />
              <span className="font-display">{playerLabel(p)}</span>
              {pending ? (
                <Badge variant="accent">Photo pending</Badge>
              ) : latest?.status === "rejected" ? (
                <Badge variant="muted">Photo not approved</Badge>
              ) : null}
              <button
                type="button"
                onClick={() => {
                  setActivePlayer(p);
                  setFile(null);
                }}
                disabled={pending}
                title={pending ? "Photo request pending" : "Request photo change"}
                aria-label={pending ? "Photo request pending" : "Request photo change"}
                className="inline-flex h-7 w-7 items-center justify-center rounded text-ink-muted transition-colors hover:bg-field hover:text-accent disabled:opacity-50"
              >
                <Camera size={16} strokeWidth={2} aria-hidden="true" />
              </button>
            </span>
          );
        })}
      </div>

      {activePlayer ? (
        <section className="rounded border border-rule bg-surface p-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded border border-rule bg-field">
              {previewUrl ? (
                <img src={previewUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                <PlayerAvatar player={activePlayer} size="lg" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-display text-sm text-ink">
                Request photo change for {activePlayer.display_name}
              </h3>
              <p className="mt-1 text-xs text-ink-muted">
                PNG, JPG, JPEG, or WebP. A coach reviews it before it appears.
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
              className="sr-only"
              onChange={(e) => setFile(e.currentTarget.files?.[0] ?? null)}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload size={16} strokeWidth={2} aria-hidden="true" />
              Pick image
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={!file || submitting}
              onClick={submit}
            >
              Send request
            </Button>
            <button
              type="button"
              onClick={reset}
              title="Close photo request"
              aria-label="Close photo request"
              className="inline-flex h-8 w-8 items-center justify-center rounded text-ink-muted hover:bg-field hover:text-ink"
            >
              <X size={16} strokeWidth={2} aria-hidden="true" />
            </button>
          </div>
        </section>
      ) : null}
    </div>
  );
}
